import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyService } from '@http/clients/company.service';
import { Company } from '@models/Company';
import { TabNames } from '@models/enums/TabNames';
import { AwaiterService } from '@services/awaiter.service';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { SectionHeaderComponent } from '@shared/section-header/section-header.component';
import { ModalService, SnackbarNotificationService } from 'ngx-lib';
import { Subscription } from 'rxjs';
import { CompaniesSingleOverviewComponent } from '../companies-single/companies-single-overview/companies-single-overview.component';

@Component({
    selector: 'app-companies-edit',
    templateUrl: './companies-edit.component.html',
    styleUrls: ['./companies-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SectionHeaderComponent, CompaniesSingleOverviewComponent]
})
export class CompaniesEditComponent implements OnInit, OnDestroy {
    @ViewChild('overview')
    overview?: CompaniesSingleOverviewComponent;

    company: Company;

    companyId: number;

    buttonLabel?: string;

    continueButtonLabel?: string;

    title?: string;

    entitySubscription?: Subscription;

    constructor(
        private readonly awaiter: AwaiterService,
        private readonly companyService: CompanyService,
        private readonly snackbarNotificationService: SnackbarNotificationService,
        private readonly modalService: ModalService,
        private readonly route: ActivatedRoute,
        private readonly router: Router
    ) {
        this.company = new Company();
        this.companyId = parseInt(this.route.snapshot.paramMap.get('id') ?? '0');
        this.buttonLabel = 'Create';
        this.continueButtonLabel = 'Continue Creation';
        this.title = 'Create New Company';
    }

    async ngOnInit(): Promise<void> {
        if (this.companyId) {
            this.buttonLabel = 'Save';
            this.continueButtonLabel = 'Continue Edition';
            this.title = 'Edit Company';
            this.entitySubscription = this.route.data.subscribe(async ({ entityData }) => {
                this.company = Company.fromInterface(entityData);
            });
        }
    }

    ngOnDestroy(): void {
        if (this.entitySubscription) {
            this.entitySubscription?.unsubscribe();
        }
    }

    async saveCompany(): Promise<void> {
        if (!this.overview?.validate()) return;

        await this.awaiter.awaitAction('Saving Company', async () => {
            await this.companyService.save(this.company);
            this.snackbarNotificationService.success('Company saved successfully.');
            this.router.navigate(
                this.companyId ? [`/companies-single/${this.companyId}/${TabNames[TabNames.Overview]}`] : ['/companies']
            );
        });
    }

    async cancel(): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Cancel Editing',
            cancelCaption: this.continueButtonLabel,
            content: 'Are you sure you want to exit? Your changes will be lost',
            title: 'Cancel Save Company'
        });

        if (responseOk) {
            this.router.navigate(
                this.companyId ? [`/companies-single/${this.companyId}/${TabNames[TabNames.Overview]}`] : ['/companies']
            );
        }
    }
}
